import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { useWorldCupGroupsQuery } from './useWorldCupGroupsQuery';
import { fetchMatchPickStats } from '../helpers/matchPickStatsApi';
import { useCompetitionContext } from '../../competitions/contexts/CompetitionContext';
import { prefetchFlagCdnForCountryCodes } from '../helpers/flagCdnPrefetch.helper';
import { useWorldCupMatchesQuery } from './useWorldCupMatchesQuery';

export type PickStatsScope = 'worldwide' | 'group';

const STALE_TIME_MS = 5 * 60_000;

/**
 * Pick distribution for one match (specs/champions-league AC-10.5).
 *
 * The competition comes from `CompetitionContext`, not a constant: both shells already
 * provide it, and `useCompetitionContext` falls back to the frozen World Cup value when no
 * provider is mounted, so stories, tests and the WC routes behave exactly as before.
 */
export function useMatchPickStats(matchId: string) {
  const apiClient = useApiClient();
  const { isLoaded, isSignedIn } = useSupabaseAuth();
  const canFetch = isLoaded && isSignedIn && matchId.length > 0;

  const { competitionId, surface } = useCompetitionContext();

  const groupsQuery = useWorldCupGroupsQuery(competitionId, canFetch);
  const matchesQuery = useWorldCupMatchesQuery(competitionId, canFetch);
  const groups = groupsQuery.data ?? [];

  const [scope, setScope] = useState<PickStatsScope>('worldwide');
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const effectiveGroupId =
    scope === 'group' ? (selectedGroupId ?? groups[0]?.id ?? null) : undefined;

  const statsQuery = useQuery({
    queryKey: ['matchPickStats', competitionId, matchId, effectiveGroupId ?? 'worldwide'],
    queryFn: () =>
      fetchMatchPickStats(
        apiClient,
        competitionId,
        matchId,
        effectiveGroupId ?? undefined,
      ),
    enabled: canFetch,
    staleTime: STALE_TIME_MS,
  });

  useEffect(() => {
    const match = statsQuery.data?.match;
    if (!match) return;
    const codes: string[] = [];
    if (match.homeCountryCode) codes.push(match.homeCountryCode);
    if (match.awayCountryCode) codes.push(match.awayCountryCode);
    if (codes.length > 0) prefetchFlagCdnForCountryCodes(codes);
  }, [statsQuery.data]);

  const handleScopeChange = useCallback(
    (nextScope: PickStatsScope) => {
      setScope(nextScope);
      if (nextScope === 'group' && selectedGroupId === null && groups.length > 0) {
        setSelectedGroupId(groups[0].id);
      }
    },
    [selectedGroupId, groups],
  );

  const handleGroupChange = useCallback((groupId: string) => {
    setSelectedGroupId(groupId);
  }, []);

  const currentMatch = matchesQuery.data?.matches.find((match) => match.matchId === matchId);
  const viewerPick = currentMatch?.prediction
    ? {
        home: currentMatch.prediction.home,
        away: currentMatch.prediction.away,
      }
    : null;

  return {
    // AC-10.6: national teams wear flags, clubs wear kits. The server derives its country
    // code from `team.country`, which RESOLVES for a club — so without this the Champions
    // League would render Barcelona under a Spanish flag rather than under no crest at all.
    crestMode: surface === 'worldcup' ? ('flag' as const) : ('kit' as const),
    data: statsQuery.data ?? null,
    isLoading: statsQuery.isLoading,
    isError: statsQuery.isError,
    viewerPick,
    isPickEditable: currentMatch?.isPickEditable ?? false,
    scope,
    groups,
    selectedGroupId: effectiveGroupId ?? null,
    onScopeChange: handleScopeChange,
    onGroupChange: handleGroupChange,
  };
}
